import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, switchMap, take } from 'rxjs';
import { debounceTime, map, shareReplay, tap } from 'rxjs/operators';
import { ValidationError } from './field-validation';
import { ObjectType, RenderableFieldInterface } from '../../tokens-and-interfaces';
import { ValidateErrorsWithFieldSchemas, CrmFormService } from '../../shared-services';

const uniquenessInvalidValidatorFn =
  (validationChanges$: Observable<ValidateErrorsWithFieldSchemas>, fieldId: string) => () => {
    return validationChanges$.pipe(
      take(1),
      map((validationErrors) => {
        const uniquenessErrorForField = validationErrors.errors.find(
          (e) => e.ruleId === 'uniqueness_enforced' && e.details?.uniquenessCheck?.fieldId === fieldId,
        );
        if (!uniquenessErrorForField) {
          return null;
        }
        return {
          uniqueness_enforced: true,
        };
      }),
    );
  };

const uniquenessWarningValidatorFn =
  (validationChanges$: Observable<ValidateErrorsWithFieldSchemas>, fieldId: string) => () => {
    return validationChanges$.pipe(
      take(1),
      map((validationErrors) => {
        const uniquenessErrorForField = validationErrors.errors.find(
          (e) => e.ruleId === 'uniqueness_warning' && e.details?.uniquenessCheck?.fieldId === fieldId,
        );
        if (!uniquenessErrorForField) {
          return null;
        }
        const existingObjectIds = uniquenessErrorForField.details?.uniquenessCheck?.existingCrmObjectIds;
        const hasErrors = existingObjectIds.length > 0;
        if (hasErrors) {
          return {
            uniqueness_warning: true,
          };
        }
        return null;
      }),
    );
  };

@Injectable()
export class ValidatorFactoryService {
  private readonly formService = inject(CrmFormService);

  private objectType!: ObjectType;
  private valueChanges$: Observable<boolean> = of(false);
  private renderableFields: RenderableFieldInterface[] = [];
  private validationChanges$!: Observable<ValidateErrorsWithFieldSchemas>;

  latestValidationErrors$$: BehaviorSubject<ValidateErrorsWithFieldSchemas | null> =
    new BehaviorSubject<ValidateErrorsWithFieldSchemas | null>(null);

  initialize(
    objectType: ObjectType,
    existingObjectId: string,
    renderableFields: RenderableFieldInterface[],
    valueChanges$: Observable<any>,
  ): void {
    this.objectType = objectType;
    this.valueChanges$ = valueChanges$.pipe(map((v) => !!v));
    this.renderableFields = renderableFields;
    this.validationChanges$ = this.valueChanges$.pipe(
      debounceTime(300),
      switchMap(() => {
        const fields = this.renderableFields.map((r) => r.fieldValue());
        return this.formService.validateCrmObject$(this.objectType, {
          crmObjectId: existingObjectId,
          fields,
        });
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      // request for validation happens in validator before loading
      // shared-components dependent upon validation data, cache manually to handle scenario
      // for uniqueness suggestions
      tap((v) => this.latestValidationErrors$$.next(v)),
    );
  }

  buildUniquenessValidatorCodeMappings(fieldId: string): { [key: string]: ValidationError } {
    return {
      uniqueness_enforced: {
        severity: 'INVALID',
        message: 'STANDARD_FIELD_RULES.UNIQUE_FIELD_VALUE.ENFORCED',
        validationFn: null,
        asyncValidationFn: uniquenessInvalidValidatorFn(this.validationChanges$, fieldId),
      },
      uniqueness_warning: {
        severity: 'WARNING',
        message: 'STANDARD_FIELD_RULES.UNIQUE_FIELD_VALUE.WARNING',
        validationFn: null,
        asyncValidationFn: uniquenessWarningValidatorFn(this.validationChanges$, fieldId),
      },
    };
  }
}
